import {useState, useEffect} from 'react'
import {useParams, useNavigate} from 'react-router-dom'

import { adminAPI, projectAPI } from '../../../api'

import { ContentFeatureSettings } from './ContentFeatureSettings'
import {API_URL} from '../../../api/endpoints'

import {Spinner} from 'react-bootstrap'
import { OverlayTrigger, Tooltip } from 'react-bootstrap'

import {CopyToClipboard} from 'react-copy-to-clipboard'

import { ConfirmModal } from '../../../components/ConfirmModal'
import { APIAccessEditModal } from './APIAccessEditModal'
import APIAccessUsage from './APIAccessUsage'
import { convertToDateTime } from '../../../utils'

import toastr from 'toastr'

interface Props {
}

const APIAccess : React.FC<Props> = ({}) => {

  const { projectId } = useParams<{ projectId: string}>();
  const [project, setProject] = useState<any>(null)
  const [defaultSettingProject, setDefaultSettingProject] = useState<any>(null)
  const isDefaultSetting = projectId === 'default'

  const navigate = useNavigate();
  const [showEditModal, setShowEditModal] = useState<boolean>(false)
  const [showDeleteModal, setShowDeleteModal] = useState<boolean>(false)

  const [showDeactivateModal, setShowDeactivateModal] = useState<boolean>(false)
  const [showRegenerateModal, setShowRegenerateModal] = useState<boolean>(false)

  const [currentTab, setCurrentTab] = useState<string>(isDefaultSetting ? 'settings' : 'overview')

  const [isCodeCopied, setIsCodeCopied] = useState(false)

  const [showKey, setShowKey] = useState<boolean>(false)


  const handleUpdateStatus = async (status) => {
    await projectAPI.updateSetting(project.id, 'api_access', {'id':project.api_access_id, 'attribute_name': 'status', 'attribute_value': status})
    toastr.info("Updated")
    setProject({...project, api_access_status: status})
  }

  const handleRegenerate = async () => {
    await projectAPI.regenerateAPIAccess(project.id)
    toastr.info("Regenerated")
    await loadProject()
  }

  useEffect(() => {
    if( !isDefaultSetting ){
      loadDefaultSettingProject()
    }
    loadProject()
  }, [projectId])

  async function loadDefaultSettingProject(){
    let {data: data} = await adminAPI.getProject('default')
    setDefaultSettingProject(data['data'])
  }

  async function loadProject(){
    if( projectId ){
      let {data: data} = await adminAPI.getProject(projectId)
      setProject(data['data'])
    }
  }

  const handleDelete = async () => {
    navigate('../api-accesses')
  }
  
  const deleteProject = () => {
    projectAPI.delete_(project.id).then(() => {
      handleDelete && handleDelete()
    })
  }

  const handleCopy = () => {
    setIsCodeCopied(true);
    setTimeout(() => {
      setIsCodeCopied(false);
    }, 5000);
  };

  return (
    <div>
      <div className="d-flex justify-content-between mb-5">
        <button className="btn btn-sm btn-light-dark" onClick={()=>navigate('../api-accesses')}>
          Back to list
        </button>
      </div>
      {project ?
        <div className="card p-0">
          <div className="card-header border-0">
            <div className="card-title align-items-start flex-column pt-5">
              {!isDefaultSetting &&
              <div>
                <span className={`mb-2 badge badge-${project.api_access_status == 'active' ? 'success' : 'danger'}`}>
                  {project.api_access_status ? project.api_access_status : 'deleted'}
                </span>
                {project.for_load_testing &&
                  <span className="badge badge-light-warning ms-1">
                    Load Testing
                  </span>
                }
              </div>
              }
              { !isDefaultSetting ? 
                <>
                  <span className={`card-label fw-bold fs-1 ${project.description && project.description != '' && 'mb-1'}`}>
                    {project.name}
                  </span>
                  {project.description && project.description != '' &&
                    <span className="text-muted fw-semibold fs-7 mt-n2 ms-1">{project.description}</span>
                  }
                </>
              :
                <span className="card-label fw-bold fs-1">
                  Default Settings
                </span>
              }
            </div>
            <div className="card-toolbar">
              { !isDefaultSetting && project.deleted_dt == null &&
                <>
                  <button className="btn btn-sm btn-light-primary me-2" onClick={()=>setShowEditModal(true)}>Edit</button>
                  { showEditModal && 
                    <APIAccessEditModal show={showEditModal} project={project} handleClose={() => setShowEditModal(false)} handleUpdated={()=> {
                      loadProject()
                      setShowEditModal(false)
                    }}/>
                  }
                  <button className="btn btn-sm btn-light-danger" onClick={()=>setShowDeleteModal(true)}>Delete</button>
                  <ConfirmModal show={showDeleteModal} handleConfirm={deleteProject} handleClose={() => setShowDeleteModal(false)}/>
                </>
              }
            </div>
          </div>
          <div className="card-body pt-0">
            {!isDefaultSetting && 
            <ul className="nav nav-tabs nav-line-tabs mb-5 fs-6">
              <li className="nav-item">
                <a className={`nav-link cursor-pointer ${currentTab === 'overview' && 'active'}`} onClick={()=>setCurrentTab('overview')}>Overview</a>
              </li>
              {!project.deleted_dt && 
                <li className="nav-item">
                  <a className={`nav-link cursor-pointer ${currentTab === 'key' && 'active'}`} onClick={()=>setCurrentTab('key')}>
                    Key
                    <span className={`mx-2 badge badge-${project.api_access_status == 'active' ? 'success' : 'danger'}`}>
                      {project.api_access_status}
                    </span>
                  </a>
                </li>
              }
              <li className="nav-item">
                <a className={`nav-link cursor-pointer ${currentTab === 'usage' && 'active'}`}  onClick={()=>setCurrentTab('usage')}>Usage</a>
              </li>
              {!project.deleted_dt && 
                <li className="nav-item">
                  <a className={`nav-link cursor-pointer ${currentTab === 'settings' && 'active'}`} onClick={()=>setCurrentTab('settings')}>Settings</a>
                </li>
              }
            </ul>
            }
            <div className="mt-5 d-flex flex-column overflow-y-auto" style={{maxHeight: `${isDefaultSetting ? '70' : '63'}vh`}}>
              { currentTab === 'overview' &&
                <>
                  <div className="d-flex flex-column">
                    <div className='fs-6 fw-bold mb-1'>{project.api_access_request_created_by_email ? 'Created' : 'Approved'} by</div>
                    <div>{project.api_access_request_created_by_email ? project.api_access_request_created_by_email : project.created_by_email}</div>
                    <div>{convertToDateTime(project.created_dt)}</div>
                  </div>
                  <div className="separator my-5"></div>
                  { project.deleted_dt && (
                    <>
                      <div className="d-flex flex-column">
                        <div className='fs-6 fw-bold mb-1'>Deleted by</div>
                        <div>{project.deleted_by_email}</div>
                        <div>{convertToDateTime(project.deleted_dt)}</div>
                      </div>
                    <div className="separator my-5"></div>
                    </>
                  )}
                  { project.api_access_request_full_name &&
                    <div className="d-flex flex-column">
                      <div className='fs-6 fw-bold mb-2'>Request Details</div>
                      <div className="d-flex flex-row mb-2">
                        <div className="w-100px fs-6">Full name</div>
                        <div>{project.api_access_request_full_name && project.api_access_request_full_name}</div>
                      </div>
                      <div className="d-flex flex-row mb-2">
                        <div className="w-100px fs-6">Email</div>
                        <div>{project.api_access_request_email && project.api_access_request_email}</div>
                      </div>
                      <div className="d-flex flex-row mb-2">
                        <div className="w-100px fs-6">Organization</div>
                        <div>{project.api_access_request_organization && project.api_access_request_organization}</div>
                      </div>
                      <div className="d-flex flex-row mb-2">
                        <div className="w-100px fs-6">Use case</div>
                        <div>{project.api_access_request_use_case && project.api_access_request_use_case}</div>
                      </div>
                      <div className="d-flex flex-row mb-2">
                        <div className="w-100px fs-6">Requested</div>
                        <div>{project.api_access_request_created_dt && convertToDateTime(project.api_access_request_created_dt)}</div>
                      </div>
                    </div>
                  }
                </>
              }
              { currentTab === 'key' &&
                <>
                { 
                  !project.deleted_dt &&
                    <>
                      <div className="d-flex flex-column mw-350px">
                        <div className="d-flex align-items-center border border-gray-300 rounded mb-1">
                          <input type={showKey ? 'text' : 'password'} className="form-control-sm border-0 form-control-transparent form-control w-100" value={project.api_key} disabled/>
                          <button type='button' className='btn btn-clean btn-sm btn-icon' onClick={()=>setShowKey(!showKey)}>
                            <i className={`fa-solid fa-${showKey ? 'eye' : 'eye-slash'} text-primary`}></i>
                          </button>
                          <CopyToClipboard text={project.api_key} onCopy={handleCopy}>
                          <button type='button' className='btn btn-clean btn-sm btn-icon'>
                            <i className={`fa-solid fa-${isCodeCopied ? 'check' : 'copy'} text-primary`}></i>
                          </button>
                          </CopyToClipboard>
                        </div>
                        <div className="d-flex flex-row justify-content-between align-items-center">
                          { project.api_access_status == 'active' &&
                          <>
                            <button type='button' className='btn btn-primary btn-sm px-2 py-1' onClick={() => setShowRegenerateModal(true)}>
                              Regenerate
                              <OverlayTrigger
                                key="regenerating"
                                placement="top"
                                overlay={
                                  <Tooltip id="regenerating">
                                    Regenerating will deactivate the current API key.
                                  </Tooltip>
                                }
                              >
                                <i className="fa-solid fa-info-circle fs-7 ms-1 pe-0"></i>
                              </OverlayTrigger>
                            </button>
                            <ConfirmModal 
                              show={showRegenerateModal} 
                              handleConfirm={()=>{
                                handleRegenerate()
                                setShowRegenerateModal(false)
                              }} 
                              handleClose={() => setShowRegenerateModal(false)}
                            />
                            <button type='button' className='btn btn-danger btn-sm px-2 py-1' onClick={()=>setShowDeactivateModal(true)}>
                              Deactivate 
                              <OverlayTrigger
                                key="deactivating"
                                placement="top"
                                overlay={
                                  <Tooltip id="deactivating">
                                    Deactivating will invalidate the API key, but it can be reactivated.
                                  </Tooltip>
                                }
                              >
                                <i className="fa-solid fa-info-circle fs-7 ms-1 pe-0"></i>
                              </OverlayTrigger>
                            </button>
                            <ConfirmModal 
                              show={showDeactivateModal} 
                              handleConfirm={()=>{
                                setShowDeactivateModal(false)
                                handleUpdateStatus('inactive')
                              }}
                              handleClose={() => setShowDeactivateModal(false)}
                            />
                          </>
                        }
                        { project.api_access_status == 'inactive' &&
                        <button type='button' className='btn btn-primary btn-sm px-2 py-1' onClick={()=>handleUpdateStatus('active')}>
                          Activate
                          <OverlayTrigger
                            key="activating"
                            placement="top"
                            overlay={
                              <Tooltip id="activating">
                                Activating will reactivate the API key.
                              </Tooltip>
                            }
                          >
                            <i className="fa-solid fa-info-circle fs-7 ms-1 pe-0"></i>
                          </OverlayTrigger>
                        </button>
                        }
                      </div>
                    </div>
                    {project.for_load_testing &&
                      <div className="bg-light-warning p-5 rounded mt-5">
                        <div className="fs-6 mb-5">
                          Load Testing Endpoint through Fanar-API (chat backend)
                          <OverlayTrigger
                            key="load-testing-endpoint"
                            placement="top"
                            overlay={
                              <Tooltip id="load-testing-endpoint">
                                This endpoint with the project ID must be used with a load-testing user account's access token.
                              </Tooltip>
                            }
                          >
                            <i className="fa-solid fa-info-circle fs-7 ms-2"></i>
                          </OverlayTrigger>
                        </div>
                        <code className="mx-0 p-3">{API_URL}/{project.id}/chats/&#123;endpoint or new or chat_id&#125;</code>            
                      </div>
                    }
                  </>
                }
                </>
              }
              { currentTab === 'settings' && 
                <ContentFeatureSettings project={project} defaultSettingProject={defaultSettingProject} setProject={setProject} />
              }
              { currentTab === 'usage' &&
                <>
                  <div className="row">
                    <div className="col-lg-2">
                      <div className="fs-6 fw-bold"># Total Requests</div>
                      <div>{project.number_of_api_requests ? project.number_of_api_requests.toLocaleString() : 0}</div>
                    </div>
                    <div className="col-lg-2">
                      <div className="fs-6 fw-bold">Latest Request On</div>
                      <div>{convertToDateTime(project.latest_api_request_dt)}</div>
                    </div>
                  </div>
                  <div className="separator my-5"></div>
                  <APIAccessUsage project={project}/>
                </>
              }
            </div>
          </div>
        </div>
        : <Spinner animation='border' className="ms-5"/>}
    </div>
  )
}

export default APIAccess